import Cookies from "js-cookie";
import { React, useState, useEffect } from "react";

// external
import Navbar from "../../Components/Navbar";
import { fetchProfile } from "../../utils/fetchProfile";
import { styled } from "@mui/material/styles";
import {
  TextField,
  Autocomplete,
  Grid,
  InputLabel,
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
} from "@mui/material";
import {
  fetchProductGroupMaster,
  fetchCreateSurveyProjectAdhoc,
  fetchDetectProjectName,
} from "./DataUtil";

//styles for open source
import "../../style/projectmanagement.css";
import { useParams } from "react-router-dom";

const BootstrapButton = styled(Button)({
  boxShadow: "none",
  textTransform: "none",
  padding: "3px 10px",
  border: "1px solid",
  lineHeight: 1.5,
  backgroundColor: "#069DAE",
  fontFamily: ["Roboto"].join(","),
  "&:hover": {
    backgroundColor: "#0696A6",
    boxShadow: "none",
  },
  "&:active": {
    boxShadow: "none",
    backgroundColor: "#0062cc",
    borderColor: "#005cbf",
  },
  "&:focus": {
    boxShadow: "0 0 0 0.2rem rgba(0,123,255,.5)",
  },
});

// Functional component for ProjectManagement
function CopyProjectAdhocPage() {
  // initial
  const { projectId } = useParams();

  const [project, setProject] = useState({});

  const symbolRegex = /[.,;:'"&]/;
  const accessToken = Cookies.get("access_token") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [dataProductGroupMasterList, setDataProductGroupMasterList] = useState(
    []
  );

  const [notAllowAccessPage, setNotAllowAccessPage] = useState(false);
  const [dataProfile, setDataProfile] = useState([]);

  const [isSourceProjectAdhoc, setIsSourceProjectAdhoc] = useState(false);

  const initialProjectTypeLabel = 'ADHOC'
  const [newProjectData, setNewProjectData] = useState({
    client_code: "2021",
    project_name: "",
    project_type: "adhoc",
    rdc_survey_start: "",
    rdc_survey_end: "",
    period: "",
    prev_period: "",
    survey_id: "",
    rdc_audit_plan_name: "",
    rdc_audit_plan_id: "",
    product_group_name: null,
    product_group_id: "",
    source_survey_id: "",
  });

  const fetchProject = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/project/${projectId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        const data = await response.json();
        setProject(data.data);
        setNewProjectData((prev) => ({
          ...prev,
          source_survey_id: data.data.survey_id,
        }));
        setIsSourceProjectAdhoc(data.data.project_type_id === "adhoc");
      } else {
        console.error("Failed to fetch Project detail:", response.statusText);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  // read
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProfile(accessToken, setDataProfile, setNotAllowAccessPage, [
        "TL",
        "COA",
        "13",
        "14",
      ]);
      fetchProductGroupMaster(
        accessToken,
        setIsLoading,
        setDataProductGroupMasterList
      );
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [accessToken]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewProjectData({
      ...newProjectData,
      [name]: value,
    });
  };

  const validProjectNameSymbolCheck = (projectName) => {
    // Validate the project name
    // symbol not allowing , . ; : \' " &
    if (symbolRegex.test(projectName)) {
      alert("Project name should not contain any symbols (, . ; : ' \" &)");
      return false;
    } else {
      return true;
    }
  };

  const PROJECT_CONFIG = {
    adhoc: {
      requiredFields: ["rdc_survey_start", "rdc_survey_end"],
      forbiddenProductGroup: "msra",
      errorMessage:
        "RDC Survey Start dan RDC Survey End tidak boleh kosong.",
      createFn: fetchCreateSurveyProjectAdhoc,
    },
  };

  const hasEmptyFields = (obj) =>
    Object.values(obj).some((val) => val === "");

  const pickFields = (obj, fields) =>
    fields.reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const requestBody = { ...newProjectData };

      const masterCheck = {
        project_name: requestBody.project_name,
        project_type: requestBody.project_type,
        product_group_name: requestBody.product_group_name,
        period: requestBody.period,
      };

      if (hasEmptyFields(masterCheck)) {
        alert("Project Name, Project Type, Period dan Product Group tidak boleh kosong.");
        return;
      }

      const config = PROJECT_CONFIG[requestBody.project_type];

      if (!config) {
        alert("ada masalah sewaktu membuat project");
        return;
      }

      // 1. Required fields validation
      const requiredData = pickFields(requestBody, config.requiredFields);
      if (hasEmptyFields(requiredData)) {
        alert(config.errorMessage);
        return;
      }

      // 2. Product group validation
      if (
        requestBody.product_group_name.includes(
          config.forbiddenProductGroup
        )
      ) {
        alert(
          `Project dengan tipe ${requestBody.project_type} tidak boleh menggunakan product group ${config.forbiddenProductGroup}`
        );
        return;
      }

      // 3. Project name validation
      const isValidName = validProjectNameSymbolCheck(
        requestBody.project_name
      );
      if (!isValidName) return;

      // 4. Duplicate check
      const allowNewProjectCreated = await fetchDetectProjectName(
        accessToken,
        requestBody.project_name
      );

      if (allowNewProjectCreated !== "") {
        alert(allowNewProjectCreated);
        return;
      }

      // 5. Execute API
      await config.createFn(accessToken, requestBody);

    } catch (error) {
      console.error("Error creating Project:", error);
    } finally {
      setIsLoading(false);
    }
  };

  let dataProductGroupMasterListFormat = dataProductGroupMasterList;

  if (newProjectData.project_type === "adhoc") {
    dataProductGroupMasterListFormat = dataProductGroupMasterList;
  }

  if (notAllowAccessPage) {
    return (
      <div>
        <Navbar
          active="Project Management"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div>Not allow to access this page</div>
      </div>
    );
  }

  if (isSourceProjectAdhoc === false) {
    return (
      <div>
        <Navbar
          active="Project Management"
          initial={dataProfile.initial}
          role_code={dataProfile.role_code}
        />
        <div style={{ padding: "16px", maxWidth: "700px", margin: "0 auto" }}>
          <h2 style={{ textAlign: "center", mt: 2, mb: 2 }}>
            Copy Project {initialProjectTypeLabel}
          </h2>
          <div>
            {isLoading ? (
              <p>Loading...</p>
            ) : (
              <p>Project yang dipilih bukan project {initialProjectTypeLabel}, tidak bisa dicopy menjadi project {initialProjectTypeLabel}</p>
            )}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                disableRipple
                sx={{
                  width: 182,
                  mt: 2,
                  mr: 2,
                  height: 50,
                  color: "#b34469",
                  borderColor: "#b34469",
                }}
                onClick={() => window.location.href = "/project-management"}
              >
                Back to Project List
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar
        active="Project Management"
        initial={dataProfile.initial}
        role_code={dataProfile.role_code}
      />
      <div style={{ padding: "16px", maxWidth: "700px", margin: "0 auto" }}>
        <h2 style={{ textAlign: "center", mt: 2, mb: 2 }}>
          Copy Project {initialProjectTypeLabel}
        </h2>

        {/* information source project */}
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 2 }}>
          <CardContent>
            {/* Header */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Source Project Information
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {/* Info Grid */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Project Name
                </Typography>
                <Typography variant="body1">
                  {project.project_name}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Project Type
                </Typography>
                <Typography variant="body1">
                  {project.project_type_id}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body1">
                  {project.project_start_at}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  End Date
                </Typography>
                <Typography variant="body1">
                  {project.project_end_at}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Product Group
                </Typography>
                <Typography variant="body1">
                  {project.product_group_master_name}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <div>
          <TextField
            variant="filled"
            name="project_name"
            label="Project Name"
            value={newProjectData.project_name}
            onChange={handleInputChange}
            sx={{ width: "100%", mb: 2 }}
          />

          {(newProjectData.project_type === "adhoc") ? (
            <Grid container spacing={2}>
              <Grid item sx={{ width: "50%" }}>
                <InputLabel htmlFor="filled-adornment-rdc_survey_start">
                  RDC Survey Start
                </InputLabel>
                <TextField
                  variant="filled"
                  name="rdc_survey_start"
                  type="date"
                  value={newProjectData.rdc_survey_start}
                  onChange={handleInputChange}
                  sx={{ width: "100%", mb: 2 }}
                />
              </Grid>
              <Grid item sx={{ width: "50%" }}>
                <InputLabel htmlFor="filled-adornment-rdc_survey_end">
                  RDC Survey End
                </InputLabel>
                <TextField
                  variant="filled"
                  name="rdc_survey_end"
                  label=""
                  type="date"
                  value={newProjectData.rdc_survey_end}
                  onChange={handleInputChange}
                  sx={{ width: "100%", mb: 2 }}
                />
              </Grid>
            </Grid>
          ) : null}
          <TextField
            variant="filled"
            name="period"
            label="Period"
            type="number"
            value={newProjectData.period}
            onChange={handleInputChange}
            sx={{ width: "100%", mb: 2 }}
          />
          {newProjectData.project_type ? (
            <Autocomplete
              id="free-solo-product_group_name"
              name="product_group_name"
              value={newProjectData.product_group_name}
              onChange={(event, newValue) => {
                setNewProjectData({
                  ...newProjectData,
                  product_group_name: newValue,
                });
              }}
              disableClearable
              options={dataProductGroupMasterListFormat.map(
                (option) => option.name
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  label="Product Group Name"
                />
              )}
            />
          ) : null}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              disableRipple
              sx={{
                width: 182,
                mt: 2,
                mr: 2,
                height: 50,
                color: "#b34469",
                borderColor: "#b34469",
              }}
              onClick={() => window.location.href = "/project-management"}
            >
              Cancel
            </Button>
            <BootstrapButton
              variant="contained"
              disableRipple
              sx={{ width: 182, mt: 2, height: 50 }}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "SUBMIT"}
            </BootstrapButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CopyProjectAdhocPage;
